import React, { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Centro por defecto España (Sevilla aprox)
const DEFAULT_CENTER = [37.39, -5.99];
const DEFAULT_ZOOM = 6;

// Icono numerado para paradas en orden (HTML divIcon, no requiere assets)
function paradaIcon(orden) {
  return L.divIcon({
    className: "ruta-parada-marker",
    html: `<div style="
      width:30px;height:30px;border-radius:50%;
      background:#f5b94a;color:#0a0a0c;
      display:flex;align-items:center;justify-content:center;
      font-weight:800;font-size:13px;font-family:sans-serif;
      border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);
    ">${orden}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

// Icono para trabajos disponibles (gris, click = añadir)
const disponibleIcon = L.divIcon({
  className: "ruta-disponible-marker",
  html: `<div style="
    width:22px;height:22px;border-radius:50%;
    background:#3f3f46;color:#e4e4e7;
    display:flex;align-items:center;justify-content:center;
    font-size:12px;font-family:sans-serif;
    border:2px solid #71717a;box-shadow:0 1px 4px rgba(0,0,0,0.3);
    cursor:pointer;
  ">+</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Componente que ajusta bounds automáticamente a todos los markers
function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function MapaRutas({ disponibles = [], paradas = [], onAddParada, onRemoveParada, height = 480 }) {
  // Separar disponibles geolocalizados (con coords) y no
  const dispGeo = useMemo(
    () => disponibles.filter((l) => l.espacio?.latitud && l.espacio?.longitud),
    [disponibles]
  );
  const sinGeo = disponibles.length - dispGeo.length;

  const paradasGeo = useMemo(
    () => paradas
      .map((p, idx) => ({ ...p, _idx: idx }))
      .filter((p) => p._meta?.espacio?.latitud && p._meta?.espacio?.longitud),
    [paradas]
  );

  const points = useMemo(() => {
    const arr = [];
    dispGeo.forEach((l) => arr.push([l.espacio.latitud, l.espacio.longitud]));
    paradasGeo.forEach((p) => arr.push([p._meta.espacio.latitud, p._meta.espacio.longitud]));
    return arr;
  }, [dispGeo, paradasGeo]);

  return (
    <div className="rounded-xl overflow-hidden border border-zinc-800" style={{ height }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ width: "100%", height: "100%", background: "#0a0a0c" }}
        scrollWheelZoom
        data-testid="rutas-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AutoFit points={points} />

        {dispGeo.map((l) => (
          <Marker
            key={`d-${l.id}`}
            position={[l.espacio.latitud, l.espacio.longitud]}
            icon={disponibleIcon}
          >
            <Popup>
              <div style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 700 }}>{l.espacio.nombre}</div>
                <div style={{ color: "#666" }}>{l.peticion?.codigo} · {l.cliente_nombre}</div>
                <div style={{ marginTop: 4 }}>{l.espacio.ciudad}, {l.espacio.provincia}</div>
                <button
                  onClick={() => onAddParada && onAddParada(l)}
                  style={{
                    marginTop: 6, padding: "4px 10px", background: "#f5b94a",
                    border: 0, borderRadius: 4, fontWeight: 600, cursor: "pointer",
                    color: "#0a0a0c", fontSize: 11,
                  }}
                >+ Añadir a la ruta</button>
              </div>
            </Popup>
          </Marker>
        ))}

        {paradasGeo.map((p) => (
          <Marker
            key={`p-${p._idx}`}
            position={[p._meta.espacio.latitud, p._meta.espacio.longitud]}
            icon={paradaIcon(p.orden)}
          >
            <Popup>
              <div style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 700 }}>#{p.orden} · {p._meta.espacio.nombre}</div>
                <div style={{ color: "#666" }}>{p._meta.peticion?.codigo}</div>
                {p.hora_estimada && <div>🕒 {p.hora_estimada}</div>}
                <button
                  onClick={() => onRemoveParada && onRemoveParada(p._idx)}
                  style={{
                    marginTop: 6, padding: "4px 10px", background: "#7f1d1d",
                    border: 0, borderRadius: 4, color: "#fff", cursor: "pointer", fontSize: 11,
                  }}
                >Quitar parada</button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {sinGeo > 0 && (
        <div className="bg-amber-500/10 border-t border-amber-500/30 px-3 py-1.5 text-[11px] text-amber-300">
          ⚠️ {sinGeo} trabajo(s) sin geolocalizar. Edita el espacio y pulsa "Localizar" para añadirlo al mapa.
        </div>
      )}
    </div>
  );
}
